// Requirements module types
// Hierarchy: Epic > Story. Epics belong to a section (overview / functional / non-functional).
// Themes are tags (many-to-many), not tree nodes.

export interface RequirementEvidenceLink {
  uri: string
  label: string
  severity?: string
  confidence?: number
}

export type RequirementType = 'epic' | 'story' | 'nfr-category'
export type RequirementPriorityLevel = 'critical' | 'high' | 'medium' | 'low'
export type RequirementStatus = 'backlog' | 'planned' | 'in-progress' | 'in-review' | 'done'
export type RequirementColumn = 'description' | 'type' | 'priority' | 'status' | 'assignee' | 'evidence' | 'themes' | 'personas' | 'completeness'

/**
 * Document-level section an epic belongs to. Stories inherit the section
 * of their parent epic. Persisted on RequirementMetadata.section.
 */
export type RequirementSection = 'overview' | 'functional' | 'non-functional'

export const REQUIREMENT_DOC_SECTION_LABELS: Record<RequirementSection, string> = {
  overview: 'Overview',
  functional: 'Functional requirements',
  'non-functional': 'Non-functional requirements',
}

export const REQUIREMENT_DOC_SECTIONS: RequirementSection[] = ['overview', 'functional', 'non-functional']

export const DEFAULT_REQUIREMENT_SECTION: RequirementSection = 'functional'

export const ROOT_REQUIREMENT_TYPES: RequirementType[] = ['epic', 'nfr-category']

export const REQUIREMENT_TYPE_BY_DEPTH: Record<number, RequirementType> = {
  0: 'epic',
  1: 'story',
}

export interface RequirementUserStory {
  role: string
  want: string
  benefit: string
}

export interface RequirementAcceptanceCriterion {
  text: string
  /**
   * New canonical field. Written by the Notes/List inline detail editor.
   * Backward compat: service reads `done ?? met ?? false` and writes both keys
   * for one release window.
   */
  done?: boolean
  /** @deprecated Use `done`. Retained for compatibility with existing rows/clients. */
  met?: boolean
}

export interface RequirementMetadata {
  /**
   * Structured user story used by the Notes/List detail panel.
   * Legacy rows may contain a plain string; the service normalizes
   * `"<sentence>"` to `{ role: '', want: '<sentence>', benefit: '' }` on read.
   */
  userStory?: RequirementUserStory | string
  useCaseFlow?: string[]
  acceptanceCriteria?: RequirementAcceptanceCriterion[]

  // ============================================================================
  // Section-typed body fields. One per template-section kind so multiple
  // description-like sections (Why + What) can coexist without collision.
  // The `description` column on requirements remains the lead body for the
  // 'description' kind. These are populated by the Granola-style notes editor.
  // ============================================================================

  /** 'why' section body. */
  why?: string
  /** 'what' section body. */
  what?: string
  /** 'open_questions' section body — markdown allowed. */
  openQuestions?: string
  /** 'custom' section bodies, keyed by template_section.id. */
  customSections?: Record<string, string>

  /**
   * Document section this epic belongs to. Stories inherit from their parent epic.
   * One of: 'overview' | 'functional' | 'non-functional'.
   * Defaults to 'functional' for new epics; 'non-functional' for nfr-category roots.
   */
  section?: RequirementSection

  owner?: string
  sprint?: string
  effort?: string
  dueDate?: string
  /**
   * @deprecated Use Requirement.personaIds / Requirement.personas (populated from
   * requirement_persona_links). Retained for historical payloads still in metadata.
   */
  linkedPersona?: {
    name: string
    role: string
    primaryNeed?: string
    keyPain?: string
    photo?: string
    sessionId?: string
  }
  [key: string]: unknown
}

export interface RequirementTheme {
  id: string
  projectId: string
  accountId: string
  name: string
  color: string | null
  description: string | null
  ownerUserId: string | null
  sortOrder: number
  createdAt: string
  updatedAt: string
}

export interface RequirementThemeRef {
  themeId: string
  isPrimary: boolean
}

export interface RequirementPersonaRef {
  personaSessionId: string
  /** Story-level primary flag. At most one per requirement (DB-enforced). */
  isPrimary: boolean
  name?: string
  role?: string
  photo?: string
}

/**
 * The effective persona set for a requirement — the union of its own
 * persona links and those of all its descendants (recursive CTE).
 * Returned by GET /:id/effective-personas. Never stored.
 */
export interface EffectivePersonaSet {
  requirementId: string
  /** Ordered: story's own primary first, then by descendant primary-vote rank, then alpha. */
  personas: RequirementPersonaRef[]
}

/** Typed kinds for template sections. 'custom' is free-form. */
export type RequirementSectionKind =
  | 'description'
  | 'why'
  | 'what'
  | 'user_story'
  | 'acceptance_criteria'
  | 'open_questions'
  | 'linked_persona'
  | 'custom'

export const REQUIREMENT_SECTION_LABELS: Record<RequirementSectionKind, string> = {
  description: 'Description',
  why: 'Why this matters',
  what: 'What it does',
  user_story: 'User story',
  acceptance_criteria: 'Acceptance criteria',
  open_questions: 'Open questions',
  linked_persona: 'Linked persona',
  custom: 'Section',
}

export interface RequirementTemplateSection {
  id: string
  templateId: string
  kind: RequirementSectionKind
  name: string
  description: string | null
  isRequired: boolean
  sortOrder: number
}

export interface RequirementTemplate {
  id: string
  accountId: string
  name: string
  description: string | null
  appliesTo: RequirementType
  isDefault: boolean
  isSystem: boolean
  sections: RequirementTemplateSection[]
  createdAt: string
  updatedAt: string
}

/**
 * Per-requirement completeness against its template. Computed on read by the
 * service — never stored. `missing` lists section kinds that are required but
 * unpopulated (used by the detail panel to render ghost-section call-outs).
 */
export interface RequirementCompleteness {
  templateId: string
  templateName: string
  /** Required sections that have data populated. */
  completed: number
  /** Total required sections in the template. 0 if the template has no required sections. */
  total: number
  /** Required section kinds that are still empty. */
  missing: RequirementSectionKind[]
}

export interface Requirement {
  id: string
  projectId: string
  accountId: string
  parentId: string | null
  /** Optional override of the account default for this requirement's type. NULL → use default. */
  templateId: string | null
  name: string
  description: string | null
  type: RequirementType
  depth: number
  priority: RequirementPriorityLevel | null
  status: RequirementStatus
  assigneeId: string | null
  assigneeName?: string | null
  sortOrder: number
  evidence: RequirementEvidenceLink[]
  metadata: RequirementMetadata
  themeIds: string[]
  primaryThemeId: string | null
  /** Personas directly linked to THIS requirement. Primary (if any) sits first. */
  personas: RequirementPersonaRef[]
  /** @deprecated Use `personas`. Convenience alias = personas.map(p => p.personaSessionId). */
  personaIds: string[]
  /** Convenience: the story's own primary persona session id; null for non-stories or stories without one. */
  primaryPersonaSessionId: string | null
  /** Computed by service at read time, not stored. Null when no template applies. */
  completeness?: RequirementCompleteness | null
  createdAt: string
  updatedAt: string
}

export interface RequirementColumnSettings {
  id: string
  projectId: string
  accountId: string
  visibleColumns: Record<string, boolean>
  createdAt: string
  updatedAt: string
}

export interface RequirementNode extends Requirement {
  children: RequirementNode[]
}

export interface RequirementsSummary {
  total: number
  byStatus: Record<RequirementStatus, number>
}

export function getAllowedRequirementTypesAtDepth(depth: number): RequirementType[] {
  if (depth === 0) return ROOT_REQUIREMENT_TYPES
  const type = REQUIREMENT_TYPE_BY_DEPTH[depth]
  return type ? [type] : []
}

export function getAllowedRequirementTypesForParent(
  parent: Pick<Requirement, 'type' | 'depth'> | null | undefined,
): RequirementType[] {
  if (!parent) return ROOT_REQUIREMENT_TYPES
  return getAllowedRequirementTypesAtDepth(parent.depth + 1)
}

export function isValidRequirementPlacement(
  type: RequirementType,
  parent: Pick<Requirement, 'type' | 'depth'> | null | undefined,
): boolean {
  return getAllowedRequirementTypesForParent(parent).includes(type)
}

/**
 * Resolve a requirement's document section.
 *
 * - Epics carry `metadata.section` directly.
 * - Stories inherit from their parent epic.
 * - NFR-category epics default to 'non-functional' if unset.
 * - Anything else defaults to 'functional'.
 */
export function getRequirementSection(
  requirement: Pick<Requirement, 'id' | 'type' | 'parentId' | 'metadata'>,
  byId?: Map<string, Pick<Requirement, 'id' | 'type' | 'metadata'>>,
): RequirementSection {
  if (requirement.type === 'story') {
    if (requirement.parentId && byId) {
      const parent = byId.get(requirement.parentId)
      if (parent) return getRequirementSection(parent as Requirement, byId)
    }
    return DEFAULT_REQUIREMENT_SECTION
  }
  const stored = (requirement.metadata as RequirementMetadata | undefined)?.section
  if (stored && (stored === 'overview' || stored === 'functional' || stored === 'non-functional')) {
    return stored
  }
  if (requirement.type === 'nfr-category') return 'non-functional'
  return DEFAULT_REQUIREMENT_SECTION
}
